import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Sphere } from "@react-three/drei";
import { forwardRef, useRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface SphereMeshProperties extends TransformableItemProperties {
  radius: number;
  widthSegments: number;
  heightSegments: number;
  materialId?: string;
}

export interface SphereMesh extends Mesh<SphereMeshProperties> {
  subtype: 'sphere';
  properties: SphereMeshProperties;
}

// Create a reusable Sphere component that can be used in both preview and world
const SphereComponent = forwardRef<THREE.Mesh, { properties: SphereMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { radius, widthSegments, heightSegments, transform, materialId } = properties;
  const { activeWorld } = useAppContext();
  
  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshStandardMaterial({ color: "#808080" });
  }, [material, material?.properties, isPreview]);

  return (
    <Sphere
      ref={ref}
      args={[radius, widthSegments, heightSegments]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const SphereMeshItem: SphereMesh = {
  id: '',
  name: 'Sphere',
  type: 'mesh',
  subtype: 'sphere',
  icon: 'Sphere',
  properties: {
    radius: 1,
    widthSegments: 32,
    heightSegments: 16,
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.radius',
      type: 'number',
      label: 'Radius',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.widthSegments',
      type: 'number',
      label: 'Width Segments',
      default: 32,
      panel: 'geometry'
    },
    {
      key: 'properties.heightSegments',
      type: 'number',
      label: 'Height Segments',
      default: 16,
      panel: 'geometry'
    }
  ],
  render: (properties: SphereMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <SphereComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
}; 